import type { FindingAttribute } from '@/models/template';
import type { TreeNode } from '../types/findingHierarchy';

const byIndex = (a: number, b: number) => a - b;

/** The dedicated class-level normal attribute. Its single statement is the
 *  "All normal" text; it's surfaced as the All Normal button, not as a row. */
const isNormalAttribute = (a: FindingAttribute): boolean =>
  a.label.trim().toLowerCase() === 'normal';

/**
 * Builds a 2-level tree from a flat list of FindingAttributes. Each attribute
 * becomes a parent node; each of its `statements` becomes a leaf whose
 * `displayValue` is the prose injected into the report.
 *
 * The class's dedicated "Normal" attribute is pulled out of the rows — its
 * single statement's text rides along on every row as `allNormalText` (the text
 * committed when "All normal" is selected).
 *
 * Attributes (rows) and statements (pills) are both sorted by `index` so the
 * picker order matches the worksheet config regardless of API order.
 *
 * Case 1: one finding-class per input.
 */
export const buildTreeFromAttributes = (
  attributes: FindingAttribute[],
  regionLabel?: string
): TreeNode[] => {
  const all = (attributes || [])
    .slice()
    .sort((a, b) => byIndex(a.index, b.index));
  const normalAttr = all.find(isNormalAttribute);
  const allNormalText = normalAttr?.statements?.[0]?.text;
  const items = all.filter((a) => !isNormalAttribute(a));
  const basePath = regionLabel ? [regionLabel] : [];

  return items.map((attribute, idx) => {
    const parentPath = [...basePath, attribute.label];
    const sortedStatements = (attribute.statements ?? [])
      .slice()
      .sort((a, b) => byIndex(a.index, b.index));
    const children: TreeNode[] = sortedStatements.map((statement, leafIdx) => {
      const leafPath = [...parentPath, statement.label];
      return {
        id: `${statement.uuid}-${leafPath.join('/')}-${leafIdx}`,
        value: statement.label,
        displayValue: statement.text,
        degree: statement.degree,
        path: leafPath,
        searchText: leafPath.join(' ').toLowerCase(),
        isLeaf: true,
        children: [],
      };
    });

    return {
      id: `${attribute.uuid}-${parentPath.join('/')}-${idx}`,
      value: attribute.label,
      path: parentPath,
      searchText: parentPath.join(' ').toLowerCase(),
      isLeaf: false,
      children,
      allNormalText,
      attrUuid: attribute.uuid,
    };
  });
};

export interface ClassLabel {
  findingClass: string;
  label: string;
}

/**
 * Case 2: multiple finding-classes per input.
 * Wraps each class's attributes in a folder node named after the finding-class.
 * Inside each folder, the normal attribute/statement tree from buildTreeFromAttributes.
 */
export const buildTreeWithClassFolders = (
  attributesByClass: Map<string, FindingAttribute[]>,
  classLabels: ClassLabel[]
): TreeNode[] =>
  classLabels.map((cl, idx) => {
    const attrs = attributesByClass.get(cl.findingClass) ?? [];
    const children = buildTreeFromAttributes(attrs);

    return {
      id: `class-${cl.findingClass}-${idx}`,
      value: cl.label,
      path: [cl.label],
      searchText: cl.label.toLowerCase(),
      isLeaf: false,
      children,
    };
  });
